// ============================================================================
// Règles des SKILLCARDS (lot 5) : ce qu'une carte apprend au moteur.
// ============================================================================
// Une SKILLCARD porte ses règles sous forme de DONNÉES (table `card_rules`),
// validées par ce schéma et rejouées dans le moteur avant publication :
//
//   formula  une égalité à rôles (« A = L × l »), instanciée dans une question
//            avec ses grandeurs (« Aire = AB × BC ») → outil `formule` ;
//            ou une formule portée par un outil dédié du moteur (Pythagore,
//            remise, discriminant) ;
//   rewrite  une réécriture vérifiée (facteur commun, a² − b², développer) ;
//   split    une égalité qui se scinde (produit nul) ;
//   schema   un raisonnement en plusieurs temps (récurrence).
//
// Seules les formules génériques sont entièrement décrites par les données ;
// les autres genres ACTIVENT un outil du moteur (catalogue fermé) et y
// ajoutent leur libellé, leur « pourquoi » et leurs pièges.
//
// Chaque règle embarque ses exemples : une règle n'est publiable que si chacun
// passe la porte de validation (`checkCardRule`). Ses pièges portent l'erreur
// type du catalogue `misconceptions` : un piège commis au brouillon devient une
// donnée de maîtrise.
// ============================================================================

import {
  KERNEL_COUNT_ENGINE_VERSION,
  KERNEL_ENGINE_VERSION,
  KERNEL_EXPLOG_ENGINE_VERSION,
  KERNEL_RADICAL_ENGINE_VERSION,
  KERNEL_GEOMETRY_ENGINE_VERSION,
  KERNEL_RECURRENCE_ENGINE_VERSION,
  KERNEL_PYTHON_ENGINE_VERSION,
  KERNEL_PYTHON_PLUS_ENGINE_VERSION,
  KERNEL_MATRIX_ENGINE_VERSION,
  KERNEL_ANALYSIS_ENGINE_VERSION,
  KERNEL_POINT_ENGINE_VERSION,
  KERNEL_ARITH_ENGINE_VERSION,
  KERNEL_LIMIT_ENGINE_VERSION,
  KERNEL_LIMIT_PLUS_ENGINE_VERSION,
  KERNEL_LIMIT_DL_ENGINE_VERSION,
  KERNEL_INTEGRAL_ENGINE_VERSION,
  KERNEL_RIEMANN_ENGINE_VERSION,
  KERNEL_ARITH_PRIMES_ENGINE_VERSION,
  KERNEL_LISTS_ENGINE_VERSION,
  SKILL_IDS,
  type FormulePred,
  type ScratchModel,
  type TrapSpec,
} from './model';
import {
  CardRuleSchema,
  RULE_ENGINE_SKILLS,
  type CardRule,
  type CardRuleExample,
} from './rules-schema';
import { lineText, parseText, replaceSym, sym, symsOf, type Line, type Node } from './tree';
import { validateModel, type ValidationIssueCode, type ValidationReport } from './validate';
import { KERNEL_STEP_IDS, kernelRuleIds } from './kernel-sheet';
import { COUNT_STEP_IDS } from './kernel-count';
import { geometryRules } from './kernel/geometry';
import { RECURRENCE_STEP_IDS } from './kernel-recurrence';
import { PYTHON_STEP_IDS } from './kernel-python';
import { MATRIX_STEP_IDS } from './kernel-matrix';
import { ANALYSIS_POINT_STEP_IDS, ANALYSIS_STEP_IDS } from './kernel-analysis';
import { ARITH_STEP_IDS } from './kernel-arith';
import { LIMIT_STEP_IDS } from './kernel-limit';
import { INTEGRAL_STEP_IDS } from './kernel-integral';

/**
 * pas de l'analyse (moteur v31) qu'une carte peut enseigner : dérivées usuelles, produit,
 * quotient, composée, primitives reconnues… (« analysis.product@1 »)
 */
export const ANALYSIS_RULE_IDS: string[] = Object.values(ANALYSIS_STEP_IDS);

/**
 * pas du calcul matriciel (moteur v30) qu'une carte peut enseigner : ligne par colonne,
 * l'inverse d'une 2 × 2, résoudre AX = B… (« matrix.product@1 »)
 */
export const MATRIX_RULE_IDS: string[] = Object.values(MATRIX_STEP_IDS);

/**
 * pas de l'arithmétique (moteur v35) qu'une carte peut enseigner : la division
 * euclidienne, PGCD(a ; b) = PGCD(b ; r), remonter l'algorithme (« arith.division@1 »)
 */
export const ARITH_RULE_IDS: string[] = Object.values(ARITH_STEP_IDS);

/**
 * pas des limites (moteur v54) qu'une carte peut enseigner : limite d'une somme, lever une
 * forme indéterminée, croissances comparées, théorème des gendarmes… (« limit.squeeze@1 ») ;
 * moteur v56 : par composition, écrire avec l'exponentielle ; moteur v58 : les développements
 * limités (DL de référence, composer, somme, produit, quotient, lire la limite)
 */
export const LIMIT_RULE_IDS: string[] = Object.values(LIMIT_STEP_IDS);

/**
 * pas des intégrales (moteur v55) qu'une carte peut enseigner : intégration par parties,
 * primitive reconnue, linéariser, Chasles… (« integral.parts@1 »)
 */
export const INTEGRAL_RULE_IDS: string[] = Object.values(INTEGRAL_STEP_IDS).filter((id) =>
  id.startsWith('integral.'),
);

/**
 * pas d'une trace Python (moteur v28) qu'une carte peut enseigner : un tour de boucle,
 * tester une condition, la valeur renvoyée… (« python.iteration@1 ») ; moteur v29 :
 * calculer, sauter des tours, compléter une ligne
 */
export const PYTHON_RULE_IDS: string[] = Object.values(PYTHON_STEP_IDS);

/** pas de la récurrence qu'une carte peut enseigner (« induction.hypothesis@1 ») */
export const RECURRENCE_RULE_IDS: string[] = Object.values(RECURRENCE_STEP_IDS).filter((id) =>
  id.startsWith('induction.'),
);

/**
 * pas du dénombrement et des seuils (moteur v27) qu'une carte peut enseigner, en plus des
 * règles du paquet counting-core (« counting.fact_quotient@1 », « kernel.integer_bound@1 »)
 */
export const COUNT_RULE_IDS: string[] = [
  ...Object.values(COUNT_STEP_IDS),
  KERNEL_STEP_IDS.integerBound,
];

/**
 * Version minimale du moteur pour une règle (`card_rules.engine_min_version`) : une
 * règle du noyau ne sert qu'aux feuilles v18 ; les autres, à toutes.
 */
export function cardRuleEngineVersion(rule: Pick<CardRule, 'kind' | 'kernel_rules'>): number {
  if (rule.kind !== 'kernel') return 1;
  const ids = rule.kernel_rules ?? [];
  // les sommes de Riemann (moteur v59) n'existent pas avant
  if (ids.some((r) => r.startsWith('riemann.'))) return KERNEL_RIEMANN_ENGINE_VERSION;
  // les intégrales (moteur v55) n'existent pas avant
  if (ids.some((r) => r.startsWith('integral.'))) return KERNEL_INTEGRAL_ENGINE_VERSION;
  // les développements limités (moteur v58) n'existent pas avant
  const limitDL: string[] = [
    LIMIT_STEP_IDS.dlReference,
    LIMIT_STEP_IDS.dlCompose,
    LIMIT_STEP_IDS.dlSum,
    LIMIT_STEP_IDS.dlScale,
    LIMIT_STEP_IDS.dlProduct,
    LIMIT_STEP_IDS.dlDivide,
    LIMIT_STEP_IDS.dlQuotient,
    LIMIT_STEP_IDS.dlLimit,
    LIMIT_STEP_IDS.sameDenominator,
  ];
  if (ids.some((r) => limitDL.includes(r))) return KERNEL_LIMIT_DL_ENGINE_VERSION;
  // les limites par composition et uᵛ = e^(v ln u) (moteur v56) n'existent pas avant
  const limitPlus: string[] = [
    LIMIT_STEP_IDS.derivativeComposite,
    LIMIT_STEP_IDS.compareComposite,
    LIMIT_STEP_IDS.expForm,
  ];
  if (ids.some((r) => limitPlus.includes(r))) return KERNEL_LIMIT_PLUS_ENGINE_VERSION;
  // les limites (moteur v54) n'existent pas avant
  if (ids.some((r) => r.startsWith('limit.'))) return KERNEL_LIMIT_ENGINE_VERSION;
  // lire ou calculer un coefficient d'une matrice (moteur v52) n'existe pas avant
  const entries: string[] = [MATRIX_STEP_IDS.entry, MATRIX_STEP_IDS.entryProduct];
  if (ids.some((r) => entries.includes(r))) return KERNEL_LISTS_ENGINE_VERSION;
  // le PGCD et le PPCM par les décompositions (moteur v51) n'existent pas avant
  const primes: string[] = [
    ARITH_STEP_IDS.decompose,
    ARITH_STEP_IDS.gcdPrimes,
    ARITH_STEP_IDS.lcmPrimes,
    ARITH_STEP_IDS.compute,
  ];
  if (ids.some((r) => primes.includes(r))) return KERNEL_ARITH_PRIMES_ENGINE_VERSION;
  // l'arithmétique (moteur v35) n'existe pas avant
  if (ids.some((r) => r.startsWith('arith.'))) return KERNEL_ARITH_ENGINE_VERSION;
  // la tangente, la condition d'une primitive (moteur v44) n'existent pas avant
  if (ids.some((r) => ANALYSIS_POINT_STEP_IDS.includes(r))) return KERNEL_POINT_ENGINE_VERSION;
  // les dérivées et primitives (moteur v31) n'existent pas avant
  if (ids.some((r) => r.startsWith('analysis.'))) return KERNEL_ANALYSIS_ENGINE_VERSION;
  // le calcul matriciel (moteur v30) n'existe pas avant
  if (ids.some((r) => r.startsWith('matrix.'))) return KERNEL_MATRIX_ENGINE_VERSION;
  // la trace d'un programme Python (moteur v28) n'existe pas avant ; calculer, sauter des
  // tours et compléter une ligne (v29) non plus
  const plus: string[] = [PYTHON_STEP_IDS.eval, PYTHON_STEP_IDS.jump, PYTHON_STEP_IDS.complete];
  if (ids.some((r) => plus.includes(r))) return KERNEL_PYTHON_PLUS_ENGINE_VERSION;
  if (ids.some((r) => r.startsWith('python.'))) return KERNEL_PYTHON_ENGINE_VERSION;
  // les pas de la récurrence (moteur v26), les règles de géométrie (v25), des racines (v20)
  // et du paquet exp / ln (v19)
  // n'existent pas avant ; le dénombrement et les seuils entiers (v27) non plus
  if (ids.some((r) => r.startsWith('counting.') || r === KERNEL_STEP_IDS.integerBound))
    return KERNEL_COUNT_ENGINE_VERSION;
  if (ids.some((r) => r.startsWith('induction.'))) return KERNEL_RECURRENCE_ENGINE_VERSION;
  if (ids.some((r) => r.startsWith('geometry.'))) return KERNEL_GEOMETRY_ENGINE_VERSION;
  if (ids.some((r) => r.startsWith('radical.'))) return KERNEL_RADICAL_ENGINE_VERSION;
  return ids.some((r) => r.startsWith('explog.'))
    ? KERNEL_EXPLOG_ENGINE_VERSION
    : KERNEL_ENGINE_VERSION;
}

/** Référence d'une règle dans un modèle de question : « <card_id>:<rule_key> ». */
export function ruleRef(cardId: string, ruleKey: string): string {
  return `${cardId}:${ruleKey}`;
}

// ── Instanciation ───────────────────────────────────────────────────────────

function renameNode(t: Node, bind: Record<string, string>): Node {
  // en deux temps, pour qu'un rôle renommé en un autre rôle ne soit pas renommé deux fois
  const roles = Object.keys(bind);
  let out = t;
  roles.forEach((r, i) => (out = replaceSym(out, r, sym('\u0002' + i))));
  roles.forEach((r, i) => (out = replaceSym(out, '\u0002' + i, sym(bind[r]!))));
  return out;
}
/**
 * Texte d'une ligne, relisible sans ambiguïté et lisible par l'élève (il peut
 * s'afficher dans un indice : « Utilise la formule : P = 2(L + l) ») : l'écriture
 * du moteur, qui ne colle jamais deux lettres (« lL » se relirait comme UNE
 * grandeur), avec « ÷ » plutôt que « / ».
 */
export function safeLineText(line: Line): string {
  return lineText(line).replace(/\//g, ' ÷ ');
}
function renameLine(text: string, bind: Record<string, string>): string {
  const line = parseText(text);
  const renamed: Line = {
    dis: line.dis.map((d) => ({ sides: d.sides.map((s) => renameNode(s, bind)) })),
  };
  return safeLineText(renamed);
}

/** Les grandeurs citées par une ligne. */
export function lineSymbols(text: string): string[] {
  const acc: Record<string, 1> = {};
  parseText(text).dis.forEach((d) => d.sides.forEach((s) => symsOf(s, acc)));
  return Object.keys(acc);
}

export interface RuleInstance {
  /** outil `formule` à ajouter au modèle (formule générique seulement) */
  pred?: FormulePred;
  /** pièges de la règle, écrits avec les grandeurs de la question */
  traps: TrapSpec[];
}

/**
 * La règle dans une question : `bind` associe chaque rôle à une grandeur du
 * modèle (« L » → « AB »). Un rôle absent de `bind` garde son nom.
 */
export function instantiateRule(
  rule: CardRule,
  bind: Record<string, string> = {},
  ref?: string,
): RuleInstance {
  const traps: TrapSpec[] = (rule.traps || []).map((t) => {
    const out: TrapSpec = { line: renameLine(t.line, bind), title: t.title, why: t.why };
    if (t.misconception_id) out.misconception_id = t.misconception_id;
    return out;
  });
  if (rule.kind !== 'formula' || rule.engine_skill) return { traps };
  const pred: FormulePred = {
    skill: 'formule',
    line: renameLine(rule.formula!, bind),
    label: rule.label,
    hora: rule.hora,
    why: rule.why,
  };
  if (rule.hint) pred.hint = rule.hint;
  if (ref) pred.rule = ref;
  return { pred, traps };
}

// ── Vérification ────────────────────────────────────────────────────────────

export type CardRuleIssueCode =
  | 'schema'
  | 'unparsable_line'
  | 'role_missing'
  | 'unused_skill'
  | 'unknown_kernel_rule'
  | ValidationIssueCode;

export interface CardRuleIssue {
  code: CardRuleIssueCode;
  message: string;
  /** index de l'exemple concerné */
  example?: number;
  line?: string;
}

export interface CardRuleReport {
  ok: boolean;
  issues: CardRuleIssue[];
  /** rapport de la porte de validation pour chaque exemple */
  examples: ValidationReport[];
}

/** Le modèle de question bâti sur un exemple de formule générique. */
export function formulaExampleModel(rule: CardRule, example: CardRuleExample): ScratchModel {
  const inst = instantiateRule(rule);
  const symbols: ScratchModel['symbols'] = {};
  Object.entries(example.given || {}).forEach(([r, v]) => (symbols[r] = { given: true, value: v }));
  Object.entries(example.find || {}).forEach(([r, v]) => (symbols[r] = { values: [v] }));
  const model: ScratchModel = {
    symbols,
    goal: Object.keys(example.find || {}),
    preds: [inst.pred!],
  };
  if (inst.traps.length) model.traps = inst.traps;
  return model;
}

/** Rejoue chaque exemple de la règle dans le moteur. Ne lance jamais. */
export function checkCardRule(input: unknown): CardRuleReport {
  const report: CardRuleReport = { ok: false, issues: [], examples: [] };
  const parsed = CardRuleSchema.safeParse(input);
  if (!parsed.success) {
    parsed.error.issues.forEach((i) =>
      report.issues.push({ code: 'schema', message: i.path.join('.') + ' : ' + i.message }),
    );
    return report;
  }
  const rule = parsed.data;
  const generic = rule.kind === 'formula' && !rule.engine_skill;

  // toutes les lignes se lisent ; la formule n'emploie que des rôles déclarés
  const lines = [...(rule.formula ? [rule.formula] : []), ...(rule.traps || []).map((t) => t.line)];
  for (const l of lines) {
    try {
      parseText(l);
    } catch {
      report.issues.push({
        code: 'unparsable_line',
        message: 'Ligne illisible par le moteur',
        line: l,
      });
    }
  }
  if (report.issues.length) return report;
  if (rule.roles) {
    lines.forEach((l) =>
      lineSymbols(l)
        .filter((s) => !rule.roles![s])
        .forEach((s) =>
          report.issues.push({
            code: 'role_missing',
            message: `« ${s} » n’est pas un rôle de la règle`,
            line: l,
          }),
        ),
    );
  }
  if (report.issues.length) return report;

  // règle du noyau : des règles connues des paquets, et des exemples qui les emploient
  if (rule.kind === 'kernel') {
    const known = new Set([
      ...kernelRuleIds(),
      ...geometryRules().map((r) => r.qid),
      ...RECURRENCE_RULE_IDS,
      ...COUNT_RULE_IDS,
      ...PYTHON_RULE_IDS,
      ...MATRIX_RULE_IDS,
      ...ANALYSIS_RULE_IDS,
      ...ARITH_RULE_IDS,
      ...LIMIT_RULE_IDS,
      ...INTEGRAL_RULE_IDS,
    ]);
    rule
      .kernel_rules!.filter((id) => !known.has(id))
      .forEach((id) =>
        report.issues.push({
          code: 'unknown_kernel_rule',
          message: `règle inconnue des paquets du noyau : ${id}`,
        }),
      );
    if (report.issues.length) return report;
    rule.examples.forEach((e, i) => {
      const model = JSON.parse(JSON.stringify(e.model)) as ScratchModel;
      if (!model.kernel) {
        report.issues.push({
          code: 'unused_skill',
          message: 'l’exemple n’est pas une feuille du noyau (clé « kernel »)',
          example: i,
        });
        return;
      }
      const r = validateModel(model);
      report.examples.push(r);
      r.issues.forEach((iss) => report.issues.push({ ...iss, example: i }));
      if (r.ok && !r.solution.some((st) => st.rule && rule.kernel_rules!.includes(st.rule)))
        report.issues.push({
          code: 'unused_skill',
          message: 'le corrigé de l’exemple n’emploie aucune règle de la carte',
          example: i,
        });
    });
    report.ok = report.issues.length === 0;
    return report;
  }

  rule.examples.forEach((e, i) => {
    let model: ScratchModel;
    if (generic) {
      const roles = lineSymbols(rule.formula!);
      const known = { ...(e.given || {}), ...(e.find || {}) };
      const missing = roles.filter((r) => known[r] === undefined);
      if (missing.length) {
        report.issues.push({
          code: 'role_missing',
          message: `exemple sans valeur pour ${missing.join(', ')}`,
          example: i,
        });
        return;
      }
      model = formulaExampleModel(rule, e);
    } else {
      model = JSON.parse(JSON.stringify(e.model)) as ScratchModel;
      const skill = rule.engine_skill!;
      const autoOn = skill === 'core' || skill === 'produitNul';
      if (!autoOn && !(model.preds || []).some((p) => p.skill === skill))
        report.issues.push({
          code: 'unused_skill',
          message: `l’exemple n’active pas l’outil ${skill}`,
          example: i,
        });
      if (e.bind && rule.traps?.length)
        model.traps = [...(model.traps || []), ...instantiateRule(rule, e.bind).traps];
    }
    const r = validateModel(model);
    report.examples.push(r);
    r.issues.forEach((iss) => report.issues.push({ ...iss, example: i }));
  });

  report.ok = report.issues.length === 0;
  return report;
}

// garde-fou : chaque outil activable par une règle existe dans le moteur
RULE_ENGINE_SKILLS.forEach((s) => {
  if (s !== 'core' && !(SKILL_IDS as readonly string[]).includes(s))
    throw new Error('outil inconnu : ' + s);
});
